import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import productionService from '../../services/productionService';
import realtimeService from '../../services/realtimeService';
import Sidebar from '../../components/ui/Sidebar';
import QuickActionsToolbar from '../../components/ui/QuickActionsToolbar';
import NotificationCenter from '../../components/ui/NotificationCenter';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import SchedulingToolbar from './components/SchedulingToolbar';
import SchedulingGrid from './components/SchedulingGrid';
import ResourcePanel from './components/ResourcePanel';
import ShiftTemplateModal from './components/ShiftTemplateModal';

const ShiftSchedulingAndResourceManagement = () => {
  const { projectId } = useParams();
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [showResourcePanel, setShowResourcePanel] = useState(true);
  const [showTemplateModal, setShowTemplateModal] = useState(false);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [viewMode, setViewMode] = useState('week');
  const [selectedProductionLine, setSelectedProductionLine] = useState('all');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  
  const [scheduleData, setScheduleData] = useState({
    workers: [],
    shifts: [],
    assignments: []
  });

  // Load initial data from Supabase
  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        const [workersData, shiftsData, assignmentsData] = await Promise.all([
          productionService?.getWorkers(),
          productionService?.getShifts(),
          productionService?.getTaskAssignments()
        ]);
        setScheduleData({
          workers: workersData,
          shifts: shiftsData,
          assignments: assignmentsData
        });
      } catch (err) {
        setError(err?.message || 'Failed to load schedule data');
        console.error('Error loading schedule data:', err);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, []);

  // Real-time subscriptions
  useEffect(() => {
    const channels = [];

    // Subscribe to workers changes
    const workersChannel = realtimeService?.subscribeToWorkers((payload) => {
      if (payload?.eventType === 'INSERT') {
        setScheduleData(prev => ({
          ...prev,
          workers: [...prev?.workers, payload?.new]
        }));
      } else if (payload?.eventType === 'UPDATE') {
        setScheduleData(prev => ({
          ...prev,
          workers: prev?.workers?.map(worker => 
            worker?.id === payload?.new?.id ? payload?.new : worker
          )
        }));
      } else if (payload?.eventType === 'DELETE') {
        setScheduleData(prev => ({
          ...prev,
          workers: prev?.workers?.filter(worker => worker?.id !== payload?.old?.id)
        }));
      }
    });
    channels?.push(workersChannel);

    // Subscribe to shifts changes
    const shiftsChannel = realtimeService?.subscribeToShifts((payload) => {
      if (payload?.eventType === 'INSERT') {
        setScheduleData(prev => ({
          ...prev,
          shifts: [...prev?.shifts, payload?.new]
        }));
      } else if (payload?.eventType === 'UPDATE') {
        setScheduleData(prev => ({
          ...prev,
          shifts: prev?.shifts?.map(shift => 
            shift?.id === payload?.new?.id ? payload?.new : shift
          )
        }));
      } else if (payload?.eventType === 'DELETE') {
        setScheduleData(prev => ({
          ...prev,
          shifts: prev?.shifts?.filter(shift => shift?.id !== payload?.old?.id)
        }));
      }
    });
    channels?.push(shiftsChannel);

    // Subscribe to task assignments changes
    const assignmentsChannel = realtimeService?.subscribeToTaskAssignments((payload) => {
      if (payload?.eventType === 'INSERT') {
        setScheduleData(prev => ({
          ...prev,
          assignments: [...prev?.assignments, payload?.new]
        }));
      } else if (payload?.eventType === 'UPDATE') {
        setScheduleData(prev => ({
          ...prev,
          assignments: prev?.assignments?.map(assignment => 
            assignment?.id === payload?.new?.id ? payload?.new : assignment
          )
        }));
      } else if (payload?.eventType === 'DELETE') {
        setScheduleData(prev => ({
          ...prev,
          assignments: prev?.assignments?.filter(assignment => assignment?.id !== payload?.old?.id)
        }));
      }
    });
    channels?.push(assignmentsChannel);

    // Cleanup subscriptions
    return () => {
      realtimeService?.unsubscribeAll(channels);
    };
  }, []);

  const handleDateChange = (newDate) => {
    setSelectedDate(newDate);
  };

  const handleViewModeChange = (mode) => {
    setViewMode(mode);
  };

  const handleProductionLineChange = (line) => {
    setSelectedProductionLine(line);
  };

  const handleAssignmentDrop = (workerId, shiftId, timeSlot) => {
    console.log('Assignment dropped:', { workerId, shiftId, timeSlot });
  };

  const handleBulkCopy = () => {
    console.log('Bulk copy shifts');
  };

  const handleAutoOptimize = () => {
    console.log('Auto optimize schedule');
  };

  return (
    <div className="min-h-screen bg-background">
      <Sidebar isCollapsed={isSidebarCollapsed} />
      <QuickActionsToolbar />
      <div style={{ marginLeft: projectId ? 'calc(var(--sidebar-width, 240px) + var(--project-submenu-width, 192px))' : 'var(--sidebar-width, 240px)' }}>
        <header className="sticky top-0 z-40 bg-card border-b border-border">
          <div className="flex items-center justify-between px-4 md:px-6 lg:px-8 py-4 md:py-6">
            <div className="flex items-center gap-4">
              <button
                className="hidden lg:flex items-center justify-center w-10 h-10 rounded-xl bg-muted hover:bg-muted/80 transition-colors duration-250"
                onClick={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
                aria-label="Toggle sidebar"
              >
                <Icon name={isSidebarCollapsed ? 'ChevronRight' : 'ChevronLeft'} size={20} />
              </button>
              <div>
                <h1 className="text-xl md:text-2xl lg:text-3xl font-semibold text-foreground">
                  Shift Scheduling & Resource Management
                </h1>
                <p className="text-sm md:text-base text-muted-foreground mt-1">
                  Manage workforce planning and task assignments across production lines
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Button
                variant="outline"
                size="sm"
                iconName="PanelRightClose"
                onClick={() => setShowResourcePanel(!showResourcePanel)}
              >
                {showResourcePanel ? 'Hide' : 'Show'} Resources
              </Button>
              <NotificationCenter />
              <button
                className="flex items-center justify-center w-10 h-10 rounded-xl bg-muted hover:bg-muted/80 transition-colors duration-250"
                onClick={() => console.log('Help')}
                aria-label="Help"
              >
                <Icon name="HelpCircle" size={20} />
              </button>
            </div>
          </div>
        </header>

        <main className="px-4 md:px-6 lg:px-8 py-6 md:py-8">
          <div className="flex flex-col lg:flex-row gap-6">
            <div className={`flex-1 transition-all duration-250 ${showResourcePanel ? 'lg:w-[calc(100%-320px)]' : 'w-full'}`}>
              <SchedulingToolbar
                selectedDate={selectedDate}
                viewMode={viewMode}
                selectedProductionLine={selectedProductionLine}
                onDateChange={handleDateChange}
                onViewModeChange={handleViewModeChange}
                onProductionLineChange={handleProductionLineChange}
                onBulkCopy={handleBulkCopy}
                onAutoOptimize={handleAutoOptimize}
                onOpenTemplates={() => setShowTemplateModal(true)}
              />
              
              <div className="mt-6">
                <SchedulingGrid
                  scheduleData={scheduleData}
                  viewMode={viewMode}
                  selectedDate={selectedDate}
                  selectedProductionLine={selectedProductionLine}
                  onAssignmentDrop={handleAssignmentDrop}
                />
              </div>
            </div>

            {showResourcePanel && (
              <div className="lg:w-80">
                <ResourcePanel
                  workers={scheduleData?.workers}
                  onClose={() => setShowResourcePanel(false)}
                />
              </div>
            )}
          </div>
        </main>
      </div>

      {showTemplateModal && (
        <ShiftTemplateModal
          onClose={() => setShowTemplateModal(false)}
          onApplyTemplate={(template) => {
            console.log('Apply template:', template);
            setShowTemplateModal(false);
          }}
        />
      )}
    </div>
  );
};

export default ShiftSchedulingAndResourceManagement;